import { IUserRepository } from "../../adapters/driven/repositories/userRepository";

export const UserServiceImplFactory = ({
  userRepository,
}: {
  userRepository: IUserRepository;
}) => {
  const getUserBetaFeatures: IGetUserBetaFeatures = async (payload) => {
    try {
      const response = await userRepository.getUserBetaFeatures(
        payload?.clinicId ? { clinic_id: payload.clinicId } : undefined
      );
      return {
        success: true,
        data: { data: response.data, clinicFeatures: response.clinic_features },
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const setUserBetaFeature: ISetUserBetaFeature = async (payload) => {
    try {
      const response = payload.enabled
        ? await userRepository.enableUserBetaFeature({
            feature: payload.feature,
            email: payload.email,
            description: payload.description,
          })
        : await userRepository.disableUserBetaFeature({
            feature: payload.feature,
          });
      if (response && typeof response.detail === "string") {
        return { success: false, error: { message: response.detail } };
      }
      return { success: true, data: response };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getPortalUserInfo: IGetPortalUserInfo = async () => {
    try {
      const response = await userRepository.getPortalUserInfo();
      const data = response?.data;
      if (data == null || typeof data !== "object") {
        return {
          success: false,
          error: { message: "Invalid user profile response" },
        };
      }
      return { success: true, data };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const updateUserInfo: IUpdateUserInfo = async (payload) => {
    try {
      const response = await userRepository.updateUserInfo(payload);
      if ("detail" in response && typeof response.detail === "string") {
        return { success: false, error: { message: response.detail } };
      }
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const signupUser: ISignupUser = async (payload) => {
    try {
      const response = await userRepository.signupUser(payload);

      // Check if the response is a success (has user_id and string message)
      if ("user_id" in response && typeof response.message === "string") {
        return {
          success: true,
          data: {
            message: response.message,
            user_id: response.user_id,
          },
        };
      }
      // Handle validation error response (has string[] message and data: null)
      else if ("message" in response && Array.isArray(response.message)) {
        return {
          success: false,
          error: {
            message: response.message,
            detail: undefined,
          },
        };
      }
      // Handle detail error response (has detail string)
      else if ("detail" in response && typeof response.detail === "string") {
        return {
          success: false,
          error: {
            message: undefined,
            detail: response.detail,
          },
        };
      }
      // Handle other message formats
      else if ("message" in response && typeof response.message === "string") {
        return {
          success: false,
          error: {
            message: response.message,
            detail: undefined,
          },
        };
      }
      // Fallback for unexpected response format
      else {
        return {
          success: false,
          error: {
            message: "Unexpected response format",
            detail: undefined,
          },
        };
      }
    } catch (error) {
      return {
        success: false,
        error: {
          message: (error as unknown as Error).message,
          detail: undefined,
        },
      };
    }
  };

  const autoBedExit: IAutoBedExit = async (payload) => {
    try {
      const response = await userRepository.autoBedExit(payload);
      if ("detail" in response && typeof response.detail === "string") {
        return { success: false, error: { message: response.detail } };
      }
      return { success: true, data: response };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const isNasaTlxPreferenceResponse = (
    r: unknown
  ): r is INasaTlxPreferenceResponse => {
    if (r == null || typeof r !== "object") return false;
    const o = r as Record<string, unknown>;
    const status = o.prompt_status;
    return (
      typeof o.has_response === "boolean" &&
      (status === "initial" ||
        status === "two_weeks" ||
        status === "thirty_days")
    );
  };

  const getNasaTlxPreference: IGetNasaTlxPreference = async () => {
    try {
      const response = await userRepository.getNasaTlxPreference();
      if (isNasaTlxPreferenceResponse(response)) {
        return { success: true, data: response };
      }
      const msg = formatNasaTlxErrorMessage(response);
      return { success: false, error: { message: msg } };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const submitNasaTlxPreference: ISubmitNasaTlxPreference = async (payload) => {
    try {
      const response = await userRepository.submitNasaTlxPreference(payload);
      if (
        response &&
        typeof response === "object" &&
        (response as INasaTlxSubmitResponseBody).success === true &&
        typeof (response as INasaTlxSubmitResponseBody).index === "number" &&
        typeof (response as INasaTlxSubmitResponseBody).next_prompt_at ===
          "string"
      ) {
        return { success: true, data: response as INasaTlxSubmitResponseBody };
      }
      const msg = formatNasaTlxErrorMessage(response);
      return { success: false, error: { message: msg } };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  return {
    getUserBetaFeatures,
    setUserBetaFeature,
    getPortalUserInfo,
    updateUserInfo,
    signupUser,
    autoBedExit,
    getNasaTlxPreference,
    submitNasaTlxPreference,
  };
};

function formatNasaTlxErrorMessage(response: unknown): string {
  if (response && typeof response === "object") {
    const o = response as Record<string, unknown>;
    if (typeof o.detail === "string") return o.detail;
    if (Array.isArray(o.detail)) {
      try {
        return JSON.stringify(o.detail);
      } catch {
        return "Validation error";
      }
    }
    if (typeof o.message === "string") return o.message;
  }
  return "NASA TLX request failed";
}
